import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Grid, Divider, Tooltip,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SEO from '../components/SEO';
import PokemonSprite from '../components/PokemonSprite';
import useDraggable from '../hooks/useDraggable';

function DraggablePaperComponent(props) {
  const { ref, style, handleMouseDown } = useDraggable({ resetOnClose: true, handleSelector: '#confirm-delete-title' });
  return <Paper ref={ref} {...props} style={{ ...props.style, ...style }} onMouseDown={handleMouseDown} />;
}

const STORAGE_KEY = 'pokepastes';

export const loadPokepastes = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const savePokepastes = (pastes) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pastes));
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      return false;
    }
    throw e;
  }
};

const parsePokemonNames = (text) => {
  const blocks = text.trim().split(/\n\s*\n/).filter(b => b.trim());
  return blocks.slice(0, 6).map(block => {
    const firstLine = block.trim().split('\n')[0].trim();
    let name = firstLine.includes(' @ ') ? firstLine.slice(0, firstLine.indexOf(' @ ')).trim() : firstLine;
    name = name.replace(/\s*\([FM]\)\s*/g, '').trim();
    return name;
  });
};

const PokepastesPage = () => {
  const [pokepastes, setPokepastes] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newText, setNewText] = useState('');
  const [addError, setAddError] = useState('');
  const [viewPaste, setViewPaste] = useState(null);
  const [copySnack, setCopySnack] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    setPokepastes(loadPokepastes());
  }, []);

  const handleAdd = () => {
    setAddError('');
    if (!newName.trim()) {
      setAddError('Please enter a name for your pokepaste.');
      return;
    }
    if (!newText.trim()) {
      setAddError('Please paste your pokepaste text.');
      return;
    }
    const blocks = newText.trim().split(/\n\s*\n/).filter(b => b.trim());
    if (blocks.length === 0) {
      setAddError('Could not parse any Pokémon from the text.');
      return;
    }
    const newPaste = {
      id: Date.now().toString(),
      name: newName.trim(),
      text: newText.trim(),
      createdAt: new Date().toISOString(),
    };
    const updated = [...pokepastes, newPaste];
    if (!savePokepastes(updated)) {
      setAddError('Storage is full. Delete some saved pokepastes before adding more.');
      return;
    }
    setPokepastes(updated);
    setAddDialogOpen(false);
    setNewName('');
    setNewText('');
  };

  const handleDelete = (id) => {
    const updated = pokepastes.filter(p => p.id !== id);
    savePokepastes(updated);
    setPokepastes(updated);
    setConfirmDeleteId(null);
  };

  const handleSaveName = () => {
    if (!editNameValue.trim() || !viewPaste) return;
    const updated = pokepastes.map(p => p.id === viewPaste.id ? { ...p, name: editNameValue.trim() } : p);
    savePokepastes(updated);
    setPokepastes(updated);
    setViewPaste(prev => ({ ...prev, name: editNameValue.trim() }));
    setEditingName(false);
  };

  const openAdd = () => {
    setNewName('');
    setNewText('');
    setAddError('');
    setAddDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <SEO
        title="My Pokepastes - Saved Teams"
        description="Manage your saved Pokémon teams in Pokepaste format. Load them instantly in the Turn Assistant."
        keywords="pokepaste, saved teams, vgc teams, competitive pokemon teams"
        noindex={true}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">My Pokepastes</Typography>
          <Typography variant="body2" color="white">
            {pokepastes.length} paste{pokepastes.length !== 1 ? 's' : ''} saved
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} size="large">
          Add team
        </Button>
      </Box>

      {pokepastes.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'rgba(255,255,255,0.3)',
            borderRadius: 3,
            backgroundColor: 'transparent',
          }}
        >
          <GroupsIcon sx={{ fontSize: 56, color: 'white', mb: 1 }} />
          <Typography variant="h6" sx={{ color: 'white' }} gutterBottom>
            No teams saved yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
            Save your Pokepastes here to load them quickly in the Turn Assistant.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add your first team
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {pokepastes.map(paste => {
            const members = parsePokemonNames(paste.text);
            return (
              <Grid item xs={12} sm={6} md={4} key={paste.id}>
                <Paper
                  elevation={2}
                  onClick={() => setViewPaste(paste)}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.2s, transform 0.15s',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #221FC7 0%, #3d3adf 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Tooltip title={paste.name} placement="top" enterDelay={500}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        noWrap
                        sx={{ color: '#fff', flex: 1 }}
                      >
                        {paste.name}
                      </Typography>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(paste.id); }}
                      sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ff6b6b' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Pokémon sprites - 2 rows of 3 */}
                  <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                    <Box sx={{ display: 'flex' }}>
                      {members.slice(0, 3).map((name, i) => (
                        <PokemonSprite key={i} pokemon={{ name }} size={56} />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {members.slice(3, 6).map((name, i) => (
                        <PokemonSprite key={i + 3} pokemon={{ name }} size={56} />
                      ))}
                    </Box>
                  </Box>

                  {/* Footer */}
                  <Divider />
                  <Box sx={{ px: 2, py: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(paste.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Detail modal */}
      <Dialog open={!!viewPaste} onClose={() => { setViewPaste(null); setEditingName(false); }} fullWidth maxWidth="sm">
        {viewPaste && (
          <>
            <DialogTitle sx={{ background: 'linear-gradient(135deg, #221FC7 0%, #3d3adf 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              {editingName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <TextField
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    size="small"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                    sx={{ flex: 1, '& input': { color: '#fff' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' }, '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.8)' } }}
                  />
                  <IconButton size="small" onClick={handleSaveName} sx={{ color: '#fff' }}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingName(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>{viewPaste.name}</Typography>
                  <IconButton size="small" onClick={() => { setEditNameValue(viewPaste.name); setEditingName(true); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>
                {new Date(viewPaste.createdAt).toLocaleDateString()}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                {parsePokemonNames(viewPaste.text).map((name, i) => (
                  <PokemonSprite key={i} pokemon={{ name }} size={64} />
                ))}
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <TextField
                value={viewPaste.text}
                multiline
                fullWidth
                minRows={10}
                maxRows={20}
                InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={() => { navigator.clipboard.writeText(viewPaste.text); setCopySnack(true); }}
                variant="outlined"
              >
                Copy
              </Button>
              <Button onClick={() => { setViewPaste(null); setEditingName(false); }} variant="outlined" color="error">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth PaperComponent={DraggablePaperComponent} aria-labelledby="confirm-delete-title">
        <DialogTitle id="confirm-delete-title" style={{ cursor: 'grab' }}>Delete paste</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this paste? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} variant="outlined">Cancel</Button>
          <Button onClick={() => handleDelete(confirmDeleteId)} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnack}
        autoHideDuration={2000}
        onClose={() => setCopySnack(false)}
        message="Pokepaste copiado al portapapeles"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Pokepaste</DialogTitle>
        <DialogContent>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <TextField
            label="Name"
            fullWidth
            value={newName}
            onChange={e => setNewName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g. VGC 2025 Sun team"
          />
          <TextField
            label="Pokepaste"
            multiline
            minRows={10}
            maxRows={20}
            fullWidth
            value={newText}
            onChange={e => setNewText(e.target.value)}
            placeholder={"Landorus @ Life Orb\nAbility: Sheer Force\nLevel: 50\nTera Type: Steel\n- Earth Power\n- Sludge Bomb\n- Substitute\n- Protect\n\n..."}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} variant="outlined" color="error">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            color="success"
            disabled={!newName.trim() || !newText.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PokepastesPage;
